import { GoogleMap } from "@react-google-maps/api";
import { forwardRef } from "react";

interface CustomGoogleMapProps {
  zoom: number;
  onClick?: (e: google.maps.MapMouseEvent) => void;
  onLoad: (map: google.maps.Map) => void | Promise<void>;
  options?: google.maps.MapOptions;
  onMouseMove?: (e: google.maps.MapMouseEvent) => void;
  onDblClick?: (e: google.maps.MapMouseEvent) => void;
  onZoomChanged?: () => void;
}

const CustomGoogleMap = forwardRef<GoogleMap | null, CustomGoogleMapProps>(
  ({ zoom, onClick, onLoad, options, onMouseMove, onDblClick, onZoomChanged }, ref) => (
    <GoogleMap
      ref={ref}
      mapContainerStyle={{
        width: "100%",
        height: "100%",
        outline: "none",
      }}
      zoom={zoom}
      onClick={(e) => {
        onClick?.(e);
      }}
      onMouseMove={onMouseMove}
      onLoad={onLoad}
      options={{ ...options, tilt: options?.tilt ?? 0, maxZoom: undefined }}
      onDblClick={onDblClick}
      onZoomChanged={onZoomChanged}
    />
  )
);

export default CustomGoogleMap;

const tileCache = new Map<string, HTMLImageElement>();

const makeTileCacheKey = (zoom: number, coord: google.maps.Point) => `${zoom}/${coord.x}/${coord.y}`;

const getGoogleTileUrl = (coord: google.maps.Point, zoom: number): string =>
  `https://mt.google.com/vt/lyrs=s&x=${coord.x}&y=${coord.y}&z=${zoom}`;

const MAX_ZOOM_BASE = 21;

export const getDeepZoomMapType = () => {
  const imageMapType = new google.maps.ImageMapType({
    getTileUrl: (coord: google.maps.Point, zoom: number): string => {
      // For zoom <= 21, return normal Google tiles
      if (zoom <= MAX_ZOOM_BASE) {
        return getGoogleTileUrl(coord, zoom);
      }

      const zoomDiff = zoom - MAX_ZOOM_BASE;
      const scale = 1 << zoomDiff; // 2^(zoomDiff)

      // Calculate corresponding tile coordinate at zoom 21
      const x = Math.floor(coord.x / scale);
      const y = Math.floor(coord.y / scale);

      // Number of tiles at zoom 21
      const maxTiles = 1 << MAX_ZOOM_BASE;

      // Wrap x coordinate horizontally (Google Maps wraps horizontally)
      const wrappedX = ((x % maxTiles) + maxTiles) % maxTiles;

      // Clamp y coordinate vertically (Google Maps tiles do NOT wrap vertically)
      const clampedY = Math.min(Math.max(y, 0), maxTiles - 1);

      //Return the tile URL at zoom 21
      return getGoogleTileUrl(new google.maps.Point(wrappedX, clampedY), MAX_ZOOM_BASE);
    },

    tileSize: new google.maps.Size(256, 256),
    maxZoom: 40,
    minZoom: 0,
  });

  // Override getTile to add CSS scaling for zoom > 21
  const originalGetTile = imageMapType.getTile.bind(imageMapType);
  imageMapType.getTile = (coord: google.maps.Point, zoom: number, ownerDocument: Document) => {
    if (zoom <= MAX_ZOOM_BASE) {
      return originalGetTile(coord, zoom, ownerDocument);
    }

    const zoomDiff = zoom - MAX_ZOOM_BASE;
    const scale = 1 << zoomDiff; // 2^zoomDiff

    // Coordinates of the base tile at zoom 21
    const baseX = Math.floor(coord.x / scale);
    const baseY = Math.floor(coord.y / scale);
    const maxTiles = 1 << MAX_ZOOM_BASE;

    const wrappedX = ((baseX % maxTiles) + maxTiles) % maxTiles;
    const clampedY = Math.min(Math.max(baseY, 0), maxTiles - 1);

    const cacheKey = makeTileCacheKey(MAX_ZOOM_BASE, new google.maps.Point(wrappedX, clampedY));

    let tile: HTMLImageElement;
    if (tileCache.has(cacheKey)) {
      tile = tileCache.get(cacheKey)!.cloneNode(true) as HTMLImageElement;
    } else {
      tile = ownerDocument.createElement("img");
      tile.src = getGoogleTileUrl(new google.maps.Point(wrappedX, clampedY), MAX_ZOOM_BASE);
      tile.style.width = "256px";
      tile.style.height = "256px";
      tile.style.position = "absolute";
      tile.style.transformOrigin = "top left";
      tile.style.imageRendering = "pixelated";
      tileCache.set(cacheKey, tile.cloneNode(true) as HTMLImageElement);
    }

    // Remainder tells where this tile is inside the scaled tile
    const offsetX = (coord.x % scale) * 256;
    const offsetY = (coord.y % scale) * 256;

    tile.style.transform = `scale(${scale})`;
    tile.style.left = `-${offsetX}px`;
    tile.style.top = `-${offsetY}px`;

    const container = ownerDocument.createElement("div");
    container.style.width = "256px";
    container.style.height = "256px";
    container.style.overflow = "hidden";
    container.style.position = "relative";

    container.appendChild(tile);
    return container;
  };

  return imageMapType;
};
